import { useState } from 'react';

import { useQueries } from '@tanstack/react-query';
import {
  getDetailBySlug,
  getGetDetailBySlugQueryKey,
  useGetRecipeProducts,
} from 'src/queries/api-ecom/generated/api-ecom';

import { ClientRecipeIngredientProduct } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/types';

const useRecipeIngredients = (slug: string, servings: number) => {
  // Les ingrédients de la recette
  const { data, isLoading } = useGetRecipeProducts(
    slug,
    { servings },
    { query: { keepPreviousData: true } },
  );

  // Les surchages de quantités sont stockées par produit
  interface Quantity {
    articleID: string;
    quantity: number;
  }
  const [quantities, setQuantities] = useState<Quantity[]>([]);

  // Pour avoir une liste de produits de remplacement qui soit réactive au changement de créneau,
  // il faut passer par React-Query. On utilise un tableau contenant un mapping entre l'ID de
  // l'article d'origine et celui de son remplacement, et on utilise useQueries pour fetcher.
  interface Replacement {
    originalID: string;
    replacementID: string;
  }
  const [replacements, setReplacements] = useState<Replacement[]>([]);
  const articleQueries = useQueries({
    queries: replacements.map(({ replacementID }) => ({
      queryKey: getGetDetailBySlugQueryKey(replacementID),
      queryFn: async () => getDetailBySlug(replacementID),
      keepPreviousData: true,
    })),
  });

  // Consolidation des données pour les ingrédients
  const ingredients: ClientRecipeIngredientProduct[] = (data?.ingredients ?? []).map(ingredient => {
    // L'article d'origine et son remplacement
    const originalArticle = ingredient.article;
    const replacedIndex = replacements.findIndex(item => item.originalID === originalArticle?.id);
    const replacedArticle = replacedIndex >= 0 ? articleQueries[replacedIndex]?.data : undefined;
    // Surcharge manuelle de la quantité, si disponible
    const quantityIndex = quantities.findIndex(item => item.articleID === originalArticle?.id);
    const quantity = quantityIndex >= 0 ? quantities[quantityIndex]?.quantity : undefined;
    // Les données sont consolidées dans l'ancien format pour la compatibilité
    // TODO: Migrer vers un format séparant les différents concepts au lieu d'avoir un objet hydride
    // P.Ex: { ingredient, article: replaced ?? original, quantity: quantity ?? original?.quantity }
    const article = replacedArticle ?? originalArticle;
    return {
      ...ingredient,
      ...(article && {
        name: article.name,
        article: {
          ...article,
          // On conserve la "substitutabilité" de l'article d'origine
          hasSubstituteProducts: originalArticle?.hasSubstituteProducts ?? true,
          // Override en prio, ou packSize du remplacement, ou quantité fournie par le back
          quantity: quantity ?? replacedArticle?.packSize ?? originalArticle?.quantity ?? 1,
        },
      }),
      ...(article !== originalArticle && { originalArticle }),
    };
  });

  // Pas de consolidation pour les fond de placard, mais la mise en place du remplacement
  // pourrait nécessiter une évolution si on désire une interface similaire aux ingrédients
  const essentials = data?.essentials ?? [];

  // Gestion du remplacement d'un ingrédient + reset de la quantité du remplacement
  const handleReplaceIngredient = (originalID: string, replacementID: Nullable<string>) => {
    setReplacements(current => [
      ...current.filter(v => v.originalID !== originalID),
      ...(replacementID != null ? [{ originalID, replacementID }] : []),
    ]);
    setQuantities(current => [...current.filter(v => v.articleID !== originalID)]);
  };

  // Gestion du changement de quantité d'un ingrédient.
  const handleUpdateIngredientQuantity = (updatedID: string, quantity: Nullable<number>) => {
    // Si on tente de mettre à jour un remplacement, on retrouve l'ID de l'article d'origine
    const replacement = replacements.find(v => v.replacementID === updatedID);
    const articleID = replacement?.originalID ?? updatedID;

    setQuantities(current => [
      ...current.filter(v => v.articleID !== articleID),
      ...(quantity != null ? [{ articleID, quantity }] : []),
    ]);
  };

  return {
    ingredients,
    essentials,
    handleReplaceIngredient,
    handleUpdateIngredientQuantity,
    isLoading,
  };
};
export default useRecipeIngredients;
